import React, { useEffect, useState, useRef } from "react";
import Pagelayout from "../components/Pagelayout";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextField,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
} from "@mui/material";

import "./payslipStyles.css";

import {
  fetchEmployeeBranches,
  fetchMultipleAPIs,
  fetchPayTemplate,
  getAllDepartmentsAPI,
  getAllDesignationsAPI,
  getEmployeeBasicDataAPI,
  listAllEmployees,
  postPayslipAPI,
  savePayslipAPI,
  singlePayslipAPI,
  updatePayslipAPI,
  deleteBatchPayslip,
} from "../parollAPIs";
import {
  getCurrentDate,
  splitDate,
  toSystemDate,
  toUTC,
} from "../../../../util/utilityFunctions";

// Custom Tab Component
const CustomTab = ({ label, isActive, onClick }) => (
  <button
    className={`custom-tab ${isActive ? "active" : ""}`}
    onClick={onClick}
  >
    {label}
  </button>
);
// Custom TabPanel Component
const CustomTabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} className="custom-tab-panel">
    {value === index && children}
  </div>
);

//main component
const CreatePayslip = () => {
  //get data from local storage - convert json to object
  const loginCred = JSON.parse(localStorage.getItem("login_meta"));
  // get employee id
  // const empId = loginCred.data._id;
  const location = useLocation();
  const singleViewId =
    location?.state?._id || localStorage.getItem("singleViewId") || null;
  // console.log("singleViewId",singleViewId);
  // const [singleViewData, setSingleViewData] = useState(null);

  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const justPostedRef = useRef(false); // Track if we just posted to prevent reload
  const initialState = {
    employee: null,
    branch: null,
    fromDate: getCurrentDate(),
    toDate: getCurrentDate(),
    designation: null,
    department: null,
    contract: null,
    sponsor: null,
    template: null,
    accountDate: getCurrentDate(),
    otherInputs: [],
    earnings: [],
    deductions: [],
    workDays: {
      totalWorkingDays: "",
      paidDays: "",
      unpaidDays: "",
    },
    isSingleView: false,
    status: "NEW",
  };
  const [employeeData, setEmployeeData] = useState(initialState);
  const [payslipBasicData, setPayslipBasicData] = useState(null);
  const [branchEmployees, setBranchEmployees] = useState([]);
  const [workDays, setWorkDays] = useState({
    totalWorkingDays: "",
    paidDays: "",
    unpaidDays: "",
  });
  const [edit, setIsEdit] = useState(false);
  const [tabs, setTabs] = useState([{ label: "WORK DAYS", id: 0 }]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [errors, setErrors] = useState({});
  const requiredFields = [
    { field: "employee", label: "Employee" },
    { field: "branch", label: "Branch" },
    { field: "fromDate", label: "From Date" },
    { field: "toDate", label: "To Date" },
    { field: "designation", label: "Designation" },
    { field: "department", label: "Department" },
    { field: "template", label: "Template" },
    { field: "accountDate", label: "Account Date" },
    { field: "totalWorkingDays", label: "Total Working Days" },
    { field: "paidDays", label: "Paid Days" },
    { field: "unpaidDays", label: "Unpaid Days" },
  ];
  const clearAllFields = () => {
    setEmployeeData(initialState);
    setWorkDays({
      totalWorkingDays: "",
      paidDays: "",
      unpaidDays: "",
    });
    setErrors({});
  };
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    requiredFields.forEach(({ field, label }) => {
      const value = employeeData[field] ?? workDays[field];

      if (value === "" || value === null) {
        errors[field] = `${label} is required`;
        isValid = false;
      } else if (
        (field === "paidDays" || field === "unpaidDays") &&
        isNaN(value)
      ) {
        errors[field] = `${label} must be a valid number`;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  // console.log("login cred data", loginCred)
  useEffect(() => {
    // Initial api call
    const apiCallsWithNames = [
      {
        name: "listBranches",
        apiCall: fetchEmployeeBranches(),
      },
      // employees will be loaded after branch selection/change below
      { name: "departmentList", apiCall: getAllDepartmentsAPI() },
      { name: "designationList", apiCall: getAllDesignationsAPI() },
    ];

    // Fetch data from API
    fetchMultipleAPIs({
      apiCallsWithNames,
      setLoading,
      setErrorMsg,
      onSuccess: (res) => {
        setPayslipBasicData(res);
        // if loginCred has a branchPk, set branch in form and load employees for it
        const defaultBranch = res?.data?.listBranches?.find(
          (b) => b?._id === loginCred?.branchPk
        );
        if (defaultBranch) {
          setEmployeeData((prev) => ({ ...prev, branch: defaultBranch }));
        }
        // Fetch templates separately; ignore errors (optional)
        fetchPayTemplate({
          body: { page: "", limit: "", search: "", category: "" },
          successCallback: (tpl) => {
            setPayslipBasicData((prev) => ({
              ...prev,
              data: {
                ...prev?.data,
                fetchPayTemplate: tpl?.data || tpl || [],
              },
            }));
          },
        });
      },
      onError: (error) => {
        console.error("Error:", error);
        // Still try fetching templates so UI can continue
        fetchPayTemplate({
          body: { page: "", limit: "", search: "", category: "" },
          successCallback: (tpl) => {
            setPayslipBasicData((prev) => ({
              ...prev,
              data: {
                ...prev?.data,
                fetchPayTemplate: tpl?.data || tpl || [],
              },
            }));
          },
        });
      },
    });
  }, []);

  // Load employees when branch changes
  useEffect(() => {
    if (employeeData?.branch?._id) {
      console.log('🔍 Branch selected, loading employees for branchId:', employeeData.branch._id);
      listAllEmployees({ branchId: employeeData.branch._id, setEmployeeList: setBranchEmployees });
    } else {
      console.log('🔍 No branch selected, clearing employee list');
      setBranchEmployees([]);
    }
  }, [employeeData?.branch?._id]);

  useEffect(() => {
    if (employeeData?.employee?._id) {
      let autoFillData;
      getEmployeeBasicDataAPI({
        body: {
          empId: employeeData?.employee?._id,
        },
        successCallback: (data) => {
          console.log('📋 Employee basic data received:', data);
          console.log('📋 Designation data from API:', data.designationData);
          console.log('📋 Department data from API:', data.departmentData);
          console.log('📋 Template data from API:', data.tempData);
          
          // Use data directly from API response, with fallback to matching from lists
          autoFillData = {
            branch: payslipBasicData?.data?.listBranches?.find(
              (branch) => branch._id === data.branchDetails?._id
            ) || null,
            // Use designation data directly from API response
            designation: data.designationData ? {
              _id: data.designationData._id,
              position: data.designationData.designation
            } : payslipBasicData?.data?.designationList?.find(
              (des) => des._id === data.designationData?._id
            ) || null,
            // Use department data directly from API response
            department: data.departmentData ? {
              _id: data.departmentData._id,
              departmentName: data.departmentData.departmentName
            } : payslipBasicData?.data?.departmentList?.find(
              (dep) => dep._id === data.departmentData?._id
            ) || null,
            // Use template data directly from API response
            template: data.tempData || null,
          };
          
          console.log('✅ Auto-filled designation:', autoFillData.designation);
          console.log('✅ Auto-filled department:', autoFillData.department);
          console.log('✅ Auto-filled template:', autoFillData.template);
          
          setWorkDays({
            totalWorkingDays: data?.workDays?.total,
            paidDays: data?.workDays?.paid,
            unpaidDays: data?.workDays?.unpaid,
          });

          // Update employeeData with autoFillData
          setEmployeeData((prevData) => ({
            ...prevData,
            ...autoFillData,
          }));

          // Clear errors for fields that are being set
          setErrors((prevErrors) => ({
            ...prevErrors,
            branch: "",
            designation: "",
            department: "",
            template: "",
            totalWorkingDays: "",
            paidDays: "",
            unpaidDays: "",
          }));
        },
        errorCallback: (error) => {
          console.log(error);
        },
      });
    }
  }, [employeeData.employee]);

  // set employee data  function based on api response
  // console.log("isSingleView",employeeData.isSingleView);
  // console.log("isSingleView",employeeData);
  const setEmployeeDataBasedOnAPI = (response) => {
    if (!response) return;
    console.log("📋 Setting employee data from API response:", response);
    console.log("📊 Status in response:", response.status);
    console.log("📊 Full response keys:", Object.keys(response));
    
    // Extract relevant fields from response
    const {
      status,
      empId,
      branchId,
      fromDate,
      toDate,
      designation,
      department,
      payTemplateId,
      accountDate,
      workDays,
      earnings,
      deductions,
      isSingleView,
      _id,
      employeeData,
      branchDetails,
      designationData,
      departmentData,
      tempData,
    } = response;

    // Find matching objects from previously fetched data OR use data from response
    const updatedData = {
      response,
      status: status,
      _id: _id || null,
      isSingleView: isSingleView || false,
      employee: employeeData 
        ? { _id: employeeData._id, staff_name: employeeData.empName, empId: employeeData.empName }
        : payslipBasicData?.data?.listAllEmployee?.find(
            (emp) => emp._id === empId
          ) || null,
      branch: branchDetails
        ? { _id: branchDetails._id, nameOfStore: branchDetails.branchName }
        : payslipBasicData?.data?.listBranches?.find(
            (branch) => branch._id === branchId
          ) || null,
      designation: designationData
        ? { _id: designationData._id, position: designationData.designation }
        : payslipBasicData?.data?.designationList?.find(
            (des) => des._id === designation
          ) || null,
      department: departmentData
        ? { _id: departmentData._id, departmentName: departmentData.departmentName }
        : payslipBasicData?.data?.departmentList?.find(
            (dep) => dep._id === department
          ) || null,
      template: tempData
        ? { _id: tempData._id, tempName: tempData.tempName }
        : payslipBasicData?.data?.fetchPayTemplate?.find(
            (temp) => temp._id === payTemplateId
          ) || null,
      fromDate: toSystemDate(fromDate),
      toDate: toSystemDate(toDate),
      accountDate: toSystemDate(accountDate),
      earnings:
        earnings?.map((item) => ({
          component: item.name,
          code: item.category,
          amount: item.monthly.toFixed(2),
        })) || [],
      deductions:
        deductions?.map((item) => ({
          component: item.name,
          code: item.category,
          amount: item.monthly.toFixed(2),
        })) || [],
    };

    // Update state with new data
    console.log("🔄 Updating employeeData with:", { status: status, ...updatedData });
    setEmployeeData((prevData) => {
      const newData = { ...prevData, ...updatedData };
      console.log("✅ New employeeData status:", newData.status);
      
      // Check status after state update
      setTimeout(() => {
        console.log("⏰ Status after timeout (should be POSTED):", newData.status);
      }, 100);
      
      return newData;
    });

    // Update work days separately
    if (workDays) {
      setWorkDays({
        totalWorkingDays: workDays.total || "",
        paidDays: workDays.paid || "",
        unpaidDays: workDays.unpaid || "",
      });
    }

    // Clear related errors
    setErrors((prevErrors) => ({
      ...prevErrors,
      branch: "",
      designation: "",
      department: "",
      template: "",
      totalWorkingDays: "",
      paidDays: "",
      unpaidDays: "",
    }));
  };

  // handle submit func
  const handleSubmit = () => {
    const { isValid, errors } = validateForm();
    setErrors(errors);

    if (!isValid) {
      return;
    }

    // Proceed with form submission
    // console.log("employeeData", employeeData);

    //api body structure
    const payload = {
      empId: employeeData?.employee?._id,
      branchId: employeeData?.branch?._id,
      fromDate: employeeData.fromDate,
      toDate: employeeData.toDate,
      designation: employeeData?.designation?._id,
      department: employeeData?.department?._id,
      payTemplateId: employeeData?.template?._id,
      accountDate: employeeData.accountDate,
      workDays: {
        total: Number(workDays.totalWorkingDays),
        paid: Number(workDays.paidDays),
        unpaid: Number(workDays.unpaidDays),
      },
    };

    // console.log("payload", payload);
    console.log("employeeData?.isSingleView", employeeData?.isSingleView);
    // call api for save data
    if (employeeData?.isSingleView === false) {
      savePayslipAPI({
        body: payload,
        successCallback: (data) => {
          // console.log("Data:", data);
          setIsEdit(true);
          setEmployeeDataBasedOnAPI(data);
          setSnackbar({
            open: true,
            message: "Payslip created successfully",
            severity: "success",
          });
          clearAllFields();
        },
        errorCallback: (error) => {
          console.error("Error:", error);
          // Extract meaningful error message from backend response
          let errorMessage = "Failed to create payslip";
          
          // Backend sends error as string directly in response.data
          if (error?.response?.data && typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error?.response?.data?.data) {
            errorMessage = error.response.data.data;
          } else if (error?.message && error.message !== 'Request failed with status code 409') {
            errorMessage = error.message;
          } else if (error?.statusCode === 409) {
            errorMessage = "Payslip already exists for this employee and date range. Please check existing payslips or use a different date range.";
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });
        },
      });
    } else {
      console.log("call update api ");
      updatePayslipAPI({
        body: { ...payload, _id: employeeData?._id },
        successCallback: (data) => {
          // console.log("Data:", data);
          setIsEdit(true);
          setEmployeeDataBasedOnAPI(data);
          setSnackbar({
            open: true,
            message: "Payslip updated successfully",
            severity: "success",
          });
          clearAllFields();
        },
        errorCallback: (error) => {
          console.error("Error:", error);
          // Extract meaningful error message from backend response
          let errorMessage = "Failed to update payslip";
          
          // Backend sends error as string directly in response.data
          if (error?.response?.data && typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error?.response?.data?.data) {
            errorMessage = error.response.data.data;
          } else if (error?.message && !error.message.includes('Request failed with status code')) {
            errorMessage = error.message;
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });
        },
      });
    }
  };

  const handleTabChange = (newValue) => setCurrentTab(newValue);

  const handleInputChange = (event, field) => {
    setWorkDays((prevData) => ({
      ...prevData,
      [field]: event.target.value,
    }));

    // Clear the error for this field if it has a value
    if (event.target.value) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "",
      }));
    }
  };

  const handleEmployeeDataChange = (field, value) => {
    if (field === "employee" && !value) {
      // If the employee field is cleared, reset all fields
      clearAllFields();
    } else {
      setEmployeeData((prevData) => ({
        ...prevData,
        [field]: value,
      }));
    }

    // Clear the error for this field if it has a value
    if (value) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "",
      }));
    }
  };

  //handle post
  const handlePostBtn = () => {
    if (employeeData.isSingleView === true) {
      // Clear any cached data for postPaySlip to ensure fresh response
      if (window.caches) {
        window.caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            window.caches.open(cacheName).then(cache => {
              cache.delete('payroll/postPaySlip').then(deleted => {
                if (deleted) {
                  console.log('🗑️ Cleared cached postPaySlip response');
                }
              });
            });
          });
        });
      }
      
      // call post api
      postPayslipAPI({
        body: { id: employeeData?._id, postDate: toUTC(getCurrentDate()) },
        successCallback: (response) => {
          console.log("✅ Payslip posted successfully, response:", response);
          justPostedRef.current = true; // Set flag to prevent reload
          setIsEdit(true);
          // The post API returns {success: true, data: {...}}
          // Extract the actual payslip data from response.data
          const payslipData = response.data || response;
          console.log("📋 Extracted payslip data:", payslipData);
          setEmployeeDataBasedOnAPI(payslipData);
          setSnackbar({
            open: true,
            message: "Payslip posted successfully",
            severity: "success",
          });
          // Don't clear fields after posting - keep the posted data visible
          // User can navigate away or create new payslip manually
          // clearAllFields(); // REMOVED - This was causing the bug
        },
        errorCallback: (error) => {
          console.error("Error:", error);
          setSnackbar({
            open: true,
            message: "Failed to post payslip",
            severity: "error",
          });
        },
      });
    }
  };

  const handleDelete = () => {
    if (!employeeData?._id) {
      setSnackbar({
        open: true,
        message: "No payslip to delete",
        severity: "warning",
      });
      return;
    }

    if (window.confirm("Are you sure you want to delete this draft payslip?")) {
      deleteBatchPayslip({
        id: employeeData._id,
        successCallback: (data) => {
          console.log("Payslip deleted:", data);
          setSnackbar({
            open: true,
            message: "Payslip deleted successfully",
            severity: "success",
          });
          // Navigate back to list after successful delete
          setTimeout(() => {
            navigate("/userdashboard/payroll/payslip");
          }, 1000);
        },
        errorCallback: (error) => {
          console.error("Delete error:", error);
          let errorMessage = "Failed to delete payslip";
          
          if (error?.response?.data && typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error?.message && !error.message.includes('Request failed with status code')) {
            errorMessage = error.message;
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });
        },
      });
    }
  };

  // console.log("employeeData?.status", employeeData?.status);
  const renderAutoComplete = (field, label, options, keyName) => {
    const safeOptions = Array.isArray(options) ? options : [];
    return (
      <Autocomplete
        options={safeOptions}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (typeof option === "string") return option;
          return (
            option[keyName] || option.staff_name || option.name || option.emp_id || ""
          );
        }}
        getOptionKey={(option) => option?._id || option}
        value={employeeData[field] || null}
        onChange={(_, newValue) => handleEmployeeDataChange(field, newValue)}
        isOptionEqualToValue={(option, value) =>
          !!value && (option?._id ? option._id === value._id : option === value)
        }
        disableClearable={false}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            placeholder={field === "template" ? "Select pay template" : ""}
            InputLabelProps={{ shrink: true }}
            error={!!errors[field]}
            helperText={errors[field] ? errors[field] : ""}
            sx={{
              '& .MuiInputBase-root': {
                minWidth:
                  field === "template"
                    ? 280
                    : field === "department" || field === "designation"
                    ? 260
                    : field === "branch"
                    ? 240
                    : 'auto',
              },
            }}
          />
        )}
        disabled={
          edit ||
          employeeData?.status === "POSTED" ||
          employeeData?.status === "DONE" ||
          field === "designation" ||
          field === "department"
        }
      />
    );
  };

  useEffect(() => {
    if (employeeData.isSingleView === true) {
      setTabs([
        { label: "WORK DAYS", id: 0 },
        { label: "SALARY CALCULATION", id: 1 },
      ]);
    } else {
      // console.log("singleViewId",singleViewId);
      // Don't reload if we just posted - the data is already updated
      if (justPostedRef.current) {
        console.log("⏭️ Skipping reload - payslip was just posted");
        justPostedRef.current = false; // Reset flag
        return;
      }
      
      if (singleViewId !== null && payslipBasicData !== null) {
        console.log("🔄 Loading payslip data for ID:", singleViewId);
        singlePayslipAPI({
          body: { _id: singleViewId },
          successCallback: (data) => {
            setIsEdit(true);

            setEmployeeDataBasedOnAPI({ ...data, isSingleView: true });
          },
          errorCallback: (error) => {
            console.error("Error:", error);
          },
        });
      } else {
        console.log("SingleViewId is null");
      }
    }
  }, [employeeData.isSingleView, payslipBasicData, singleViewId]);

  //
  const handleIsEdit = () => {
    setIsEdit(!edit);
  };

  // console.log(employeeData)
  return (
    <Pagelayout
      handleBack={() => navigate("/userdashboard/payroll/payslip")}
      headerLayout={2}
      newData={!edit}
      actionBtn={
        employeeData?.status !== "POSTED" && employeeData?.status !== "DONE"
      }
      handleSave={handleSubmit}
      snackbar={snackbar}
      setSnackbar={setSnackbar}
      isLoading={loading}
      handleEdit={handleIsEdit}
      postBtn={
        employeeData?.status !== "POSTED" && employeeData?.status !== "DONE"
      }
      handlePost={handlePostBtn}
      status={employeeData?.status}
      deleteBtn={employeeData?.status === "DRAFT" && employeeData?._id}
      handleDelete={handleDelete}
    >
      <div>
        {/* {employeeData?.name && (
          <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
            {employeeData.name}
          </Typography>
        )} */}

        <Grid container spacing={2} mb={4}>
          {/* Branch first */}
          <Grid item xs={12} sm={6} md={3}>
            {renderAutoComplete(
              "branch",
              "Branch",
              payslipBasicData?.data?.listBranches || [],
              "branchName"
            )}
          </Grid>
          {/* Employee second */}
          <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 280 }}>
            <Autocomplete
              options={Array.isArray(branchEmployees) ? branchEmployees : []}
              getOptionLabel={(option) => {
                if (!option) return "";
                if (typeof option === "string") return option;
                return option.staff_name || option.name || option.emp_id || "";
              }}
              value={employeeData["employee"] || null}
              onChange={(_, newValue) => {
                console.log('👤 Employee selected:', newValue);
                handleEmployeeDataChange("employee", newValue);
              }}
              isOptionEqualToValue={(option, value) =>
                !!value && (option?._id ? option._id === value._id : option === value)
              }
              disableClearable={false}
              disabled={!employeeData?.branch?._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors["employee"]}
                  helperText={
                    !employeeData?.branch?._id
                      ? "Select a branch to load employees"
                      : errors["employee"]
                      ? errors["employee"]
                      : branchEmployees.length === 0
                      ? "No employees found for this branch"
                      : ""
                  }
                  sx={{
                    '& .MuiInputBase-root': {
                      minWidth: 320,
                    },
                  }}
                />
              )}
            />
          </Grid>
          {["fromDate", "toDate"].map((field) => (
            <Grid item xs={12} sm={6} md={2.5} key={field}>
              <TextField
                fullWidth
                size="small"
                label={field === "fromDate" ? "From" : "To"}
                type="date"
                value={employeeData[field]}
                onChange={(e) =>
                  handleEmployeeDataChange(field, e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                error={!!errors[field]}
                helperText={errors[field] ? errors[field] : ""}
                disabled={
                  employeeData?.status === "POSTED" ||
                  employeeData?.status === "DONE" ||
                  edit
                }
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            {renderAutoComplete(
              "designation",
              "Designation",
              payslipBasicData?.data?.designationList || [],
              "position"
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderAutoComplete(
              "department",
              "Department",
              payslipBasicData?.data?.departmentList || [],
              "departmentName"
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Template"
              value={employeeData.template?.tempName || ""}
              InputLabelProps={{ shrink: true }}
              disabled
              placeholder="No template assigned"
              sx={{
                '& .MuiInputBase-root': {
                  minWidth: 200,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Account Date"
              type="date"
              value={employeeData.accountDate}
              onChange={(e) =>
                handleEmployeeDataChange("accountDate", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
              error={!!errors.accountDate}
              helperText={errors.accountDate ? errors.accountDate : ""}
              disabled={
                employeeData?.status === "POSTED" ||
                employeeData?.status === "DONE" ||
                edit
              }
            />
          </Grid>
        </Grid>

        {/* Custom Tabs */}
        <div className="">
          <div className="tab-wrapper-py">
            {tabs.map((tab) => (
              <CustomTab
                key={tab.id}
                label={tab.label}
                isActive={currentTab === tab.id}
                onClick={() => handleTabChange(tab.id)}
              />
            ))}
          </div>
        </div>

        <CustomTabPanel value={currentTab} index={0}>
          <Grid container spacing={2}>
            {[
              { label: "Total Working Days", field: "totalWorkingDays" },
              { label: "Paid Days", field: "paidDays" },
              { label: "Unpaid Days", field: "unpaidDays" },
            ].map(({ label, field }) => (
              <Grid item xs={12} sm={4} key={field}>
                <TextField
                  fullWidth
                  label={label}
                  size="small"
                  type="number"
                  value={workDays[field]}
                  onChange={(event) => handleInputChange(event, field)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!errors[field]}
                  helperText={errors[field] ? errors[field] : ""}
                  disabled={
                    employeeData?.status === "POSTED" ||
                    employeeData?.status === "DONE" ||
                    edit
                  }
                />
              </Grid>
            ))}
          </Grid>
        </CustomTabPanel>
        <CustomTabPanel value={currentTab} index={1}>
          {["earnings", "deductions"].map((section) => (
            <TableContainer
              key={section}
              component={Paper}
              sx={{ maxHeight: 300, mt: 2 }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Amount</TableCell>
                    {/* <TableCell>Action</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeData[section].map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.component}</TableCell>
                      <TableCell>{item.code}</TableCell>
                      <TableCell>{item.amount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {/* Sticky Footer Row */}
                <TableFooter>
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "white",
                        fontWeight: "bold",
                      }}
                    >
                      TOTAL {section.toUpperCase()}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "white",
                        fontWeight: "bold",
                      }}
                    >
                      {employeeData[section]
                        .reduce((sum, item) => sum + parseFloat(item.amount), 0)
                        .toFixed(2)}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "white",
                      }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          ))}
        </CustomTabPanel>

        {currentTab === 1 && (
          <Paper sx={{ p: 2, mt: 4 }}>
            <Grid container>
              <Grid item xs={8}>
                <Typography variant="h6">NET SALARY</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" align="right">
                  {employeeData.earnings.reduce(
                    (sum, item) => sum + parseFloat(item.amount),
                    0
                  ) -
                    employeeData.deductions.reduce(
                      (sum, item) => sum + parseFloat(item.amount),
                      0
                    )}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
      </div>
    </Pagelayout>
  );
};

export default CreatePayslip;
